/*
 * Google Search Console Indexing Manager UI.
 *  - `/admin/sites/:tenantId/indexing`  → main dashboard
 *  - `/auth/google-search-console`      → OAuth callback lander (posts code back)
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS.
 * The redirect_uri is always `window.location.origin + '/auth/google-search-console'`.
 * This is what the backend expects and what Google Cloud must have on file.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Search, Link2, RefreshCcw, Trash2, LogOut, Plus, ExternalLink,
  AlertTriangle, CheckCircle2, Loader2, ShieldCheck, ShieldAlert, Clock,
} from "lucide-react";

// ── Callback page: posts ?code&state back to backend then closes/redirects
export function GscOAuthCallback() {
  const [params] = useSearchParams();
  const [msg, setMsg] = useState("Finalizing Google connection…");
  const nav = useNavigate();

  useEffect(() => {
    const run = async () => {
      const code = params.get("code");
      const state = params.get("state");
      const err = params.get("error");
      if (err) {
        setMsg(`Google returned: ${err}. You can close this tab.`);
        return;
      }
      if (!code || !state) {
        setMsg("Missing code/state from Google.");
        return;
      }
      // Recover tenant from state ("tenantId:nonce.mac")
      const tenantId = String(state).split(":")[0];
      const redirect_uri = window.location.origin + "/auth/google-search-console";
      try {
        await api.post(`/admin/sites/${tenantId}/indexing/callback`, { code, state, redirect_uri });
        setMsg("Connected. Redirecting to Indexing Manager…");
        setTimeout(() => nav(`/admin/sites/${tenantId}/indexing`, { replace: true }), 700);
      } catch (e) {
        setMsg(e?.response?.data?.detail || "Google connection failed. Try again from the admin panel.");
      }
    };
    run();
  }, []);

  return (
    <main className="min-h-screen bg-black text-white flex items-center justify-center px-6" data-testid="gsc-callback">
      <div className="text-center">
        <Loader2 className="mx-auto animate-spin text-blue-400" />
        <div className="mt-4 text-sm text-white/70">{msg}</div>
      </div>
    </main>
  );
}

// ── Main dashboard
export default function SitesIndexing() {
  const { tenantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [sitemaps, setSitemaps] = useState(null);
  const [urls, setUrls] = useState([]);
  const [inspectUrl, setInspectUrl] = useState("");
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [filter, setFilter] = useState({ verdict: "", coverage: "" });

  const propertyUrl = status?.connection?.property_url;
  const connected = !!status?.connected;
  const verified = !!status?.connection?.property_verified;

  const loadAll = async () => {
    setLoading(true); setError("");
    try {
      const s = await api.get(`/admin/sites/${tenantId}/indexing/status`);
      setStatus(s.data);
      if (s.data?.connected && s.data.connection?.property_url) {
        const [smaps, u] = await Promise.all([
          api.get(`/admin/sites/${tenantId}/indexing/sitemaps`),
          api.get(`/admin/sites/${tenantId}/indexing/urls`, { params: filter }),
        ]);
        setSitemaps(smaps.data);
        setUrls(u.data?.urls || []);
      } else {
        setSitemaps(null); setUrls([]);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load Search Console data.");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, [tenantId]);

  const connect = async () => {
    setBusy("connect"); setError("");
    try {
      const { data } = await api.get(`/admin/sites/${tenantId}/indexing/oauth-url`);
      window.location.href = data.oauth_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start Google OAuth (server may be missing GOOGLE_CLIENT_ID).");
      setBusy("");
    }
  };

  const pickProperty = async (property_url) => {
    setBusy("property"); setError("");
    try {
      await api.post(`/admin/sites/${tenantId}/indexing/property`, { property_url });
      await loadAll();
    } catch (e) { setError(e?.response?.data?.detail || "Could not select property."); }
    finally { setBusy(""); }
  };

  const submitSitemap = async () => {
    if (!sitemapUrl.trim()) return;
    setBusy("sitemap"); setError("");
    try {
      await api.post(`/admin/sites/${tenantId}/indexing/sitemaps`, { sitemap_url: sitemapUrl.trim() });
      setSitemapUrl("");
      await loadAll();
    } catch (e) { setError(e?.response?.data?.detail || "Sitemap submit failed."); }
    finally { setBusy(""); }
  };

  const deleteSitemap = async (u) => {
    if (!window.confirm(`Remove ${u} from Search Console?`)) return;
    setBusy("sitemap-del");
    try {
      await api.delete(`/admin/sites/${tenantId}/indexing/sitemaps`, { params: { sitemap_url: u } });
      await loadAll();
    } catch (e) { setError(e?.response?.data?.detail || "Delete failed."); }
    finally { setBusy(""); }
  };

  const runInspection = async () => {
    if (!inspectUrl.trim()) return;
    setBusy("inspect"); setError("");
    try {
      const { data } = await api.post(`/admin/sites/${tenantId}/indexing/inspect`, { url: inspectUrl.trim() });
      if (!data.ok) setError(`${data.error}${data.status ? ` (${data.status})` : ""}`);
      setInspectUrl("");
      const u = await api.get(`/admin/sites/${tenantId}/indexing/urls`, { params: filter });
      setUrls(u.data?.urls || []);
    } catch (e) { setError(e?.response?.data?.detail || "URL inspection failed."); }
    finally { setBusy(""); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google and clear cached URL statuses for this tenant?")) return;
    setBusy("disconnect");
    try { await api.post(`/admin/sites/${tenantId}/indexing/disconnect`); await loadAll(); }
    catch (e) { setError(e?.response?.data?.detail || "Disconnect failed."); }
    finally { setBusy(""); }
  };

  const filteredUrls = useMemo(() => urls, [urls]);

  return (
    <main className="min-h-screen bg-black text-white" data-testid="sites-indexing-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-10 pb-16">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-black text-blue-400 mb-2">
              Search Console · Indexing Manager
            </div>
            <h1 className="font-black text-2xl md:text-3xl leading-tight" data-testid="indexing-title">
              Tenant · {tenantId}
            </h1>
            <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-white/40">
              {connected ? `Connected as ${status?.connection?.google_email || "—"}` : "Not connected"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadAll} disabled={loading}
                    className="h-9 px-3 rounded border border-white/15 text-[10px] uppercase tracking-[0.22em] font-black inline-flex items-center gap-1.5 hover:border-white"
                    data-testid="btn-refresh">
              <RefreshCcw size={12} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            {connected && (
              <button onClick={disconnect} disabled={busy === "disconnect"}
                      className="h-9 px-3 rounded border border-red-500/50 text-red-300 text-[10px] uppercase tracking-[0.22em] font-black inline-flex items-center gap-1.5 hover:bg-red-500/10"
                      data-testid="btn-disconnect">
                <LogOut size={12} /> Disconnect
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-2 rounded border border-red-500/40 bg-red-500/5 p-3 text-sm text-red-300" data-testid="indexing-error">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" /> <div>{error}</div>
          </div>
        )}

        {/* Not connected — connect flow */}
        {!connected && !loading && (
          <div className="rounded-xl border border-white/10 p-8 bg-white/[0.02]" data-testid="connect-panel">
            <div className="text-lg font-black mb-2">Connect a Google account for this tenant.</div>
            <p className="text-sm text-white/70 max-w-[640px]">
              The connecting Google account must already be a <strong>verified owner or user</strong> of the Search Console property for this tenant.
              We only read Search Console data; we do not use the Indexing API for ordinary pages.
            </p>
            <button onClick={connect} disabled={busy === "connect"}
                    className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded font-black uppercase tracking-[0.22em] text-[11px] bg-blue-500 text-black hover:bg-blue-400"
                    data-testid="btn-connect-google">
              {busy === "connect" ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
              Connect Google
            </button>
          </div>
        )}

        {/* Connected — pick property + verification checklist */}
        {connected && !propertyUrl && (
          <div className="rounded-xl border border-white/10 p-6 bg-white/[0.02]" data-testid="pick-property-panel">
            <div className="text-lg font-black mb-2">Select a verified property to manage.</div>
            <p className="text-sm text-white/70 mb-4">
              Properties visible on {status?.connection?.google_email}. Only <span className="text-emerald-400 font-black">verified</span> ones are actionable.
            </p>
            <VerificationChecklist />
            <div className="mt-4 divide-y divide-white/5 border border-white/10 rounded-lg overflow-hidden">
              {(status?.google_properties || []).length === 0 ? (
                <div className="p-4 text-sm text-white/60">No properties returned. Add this Google account to the Search Console property first.</div>
              ) : (
                (status?.google_properties || []).map((p) => (
                  <div key={p.siteUrl} className="flex items-center justify-between p-3 gap-3">
                    <div>
                      <div className="text-sm font-black" data-testid={`prop-${p.siteUrl}`}>{p.siteUrl}</div>
                      <div className="text-[11px] text-white/50">Permission: {p.permissionLevel || "unknown"}</div>
                    </div>
                    <button onClick={() => pickProperty(p.siteUrl)}
                            disabled={busy === "property" || p.permissionLevel === "siteUnverifiedUser"}
                            className="h-9 px-4 rounded font-black uppercase tracking-[0.22em] text-[10px] bg-blue-500 text-black disabled:opacity-40"
                            data-testid={`pick-${p.siteUrl}`}>
                      Manage
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Property assigned — main dashboard */}
        {connected && propertyUrl && (
          <>
            <div className="rounded-xl border border-white/10 p-5 bg-white/[0.02] mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="active-property">
              <div className="flex items-center gap-2">
                {verified
                  ? <ShieldCheck size={18} className="text-emerald-400" />
                  : <ShieldAlert size={18} className="text-yellow-400" />}
                <div>
                  <div className="text-sm font-black">{propertyUrl}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
                    {verified ? "Verified" : "NOT VERIFIED — actions may fail"}
                  </div>
                </div>
              </div>
              <a href={`https://search.google.com/search-console?resource_id=${encodeURIComponent(propertyUrl)}`}
                 target="_blank" rel="noreferrer"
                 className="text-[11px] uppercase tracking-[0.22em] font-black inline-flex items-center gap-1 hover:text-blue-400"
                 data-testid="open-gsc">
                Open in Search Console <ExternalLink size={12} />
              </a>
            </div>

            {/* Sitemaps */}
            <section className="rounded-xl border border-white/10 p-5 bg-white/[0.02] mb-6" data-testid="sitemaps-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="font-black text-sm">Sitemaps</div>
                <div className="flex gap-2">
                  <input value={sitemapUrl} onChange={(e) => setSitemapUrl(e.target.value)}
                         placeholder="https://plugitai.com/sitemap.xml"
                         className="h-9 w-72 bg-black border border-white/15 rounded px-3 text-xs outline-none focus:border-blue-400"
                         data-testid="input-sitemap-url" />
                  <button onClick={submitSitemap} disabled={busy === "sitemap" || !sitemapUrl.trim()}
                          className="h-9 px-3 rounded bg-blue-500 text-black font-black uppercase tracking-[0.22em] text-[10px] inline-flex items-center gap-1 disabled:opacity-40"
                          data-testid="btn-submit-sitemap">
                    <Plus size={12} /> Submit
                  </button>
                </div>
              </div>
              <div className="divide-y divide-white/5 border border-white/10 rounded overflow-hidden">
                {(sitemaps?.sitemap || []).length === 0 ? (
                  <div className="p-3 text-sm text-white/50">No sitemaps registered on this property.</div>
                ) : (sitemaps.sitemap.map((s) => (
                  <div key={s.path} className="flex items-center justify-between p-3 gap-3 text-sm" data-testid={`sitemap-${s.path}`}>
                    <div className="min-w-0">
                      <div className="font-black truncate">{s.path}</div>
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
                        {s.lastSubmitted ? `Submitted ${new Date(s.lastSubmitted).toLocaleDateString()}` : "—"}
                        {s.warnings ? ` · ${s.warnings} warnings` : ""}
                        {s.errors ? ` · ${s.errors} errors` : ""}
                      </div>
                    </div>
                    <button onClick={() => deleteSitemap(s.path)}
                            className="text-red-300 hover:text-red-400"
                            data-testid={`sitemap-del-${s.path}`}
                            aria-label="Delete sitemap">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )))}
              </div>
            </section>

            {/* URL inspection queue */}
            <section className="rounded-xl border border-white/10 p-5 bg-white/[0.02]" data-testid="urls-panel">
              <div className="flex flex-wrap items-center gap-3 justify-between mb-4">
                <div className="font-black text-sm inline-flex items-center gap-2">
                  <Search size={14} /> URL Priority Queue
                </div>
                <div className="flex gap-2">
                  <select value={filter.verdict} onChange={(e) => setFilter({ ...filter, verdict: e.target.value })}
                          className="h-9 bg-black border border-white/15 rounded px-2 text-xs" data-testid="filter-verdict">
                    <option value="">All verdicts</option>
                    <option value="PASS">Indexed</option>
                    <option value="PARTIAL">Partial</option>
                    <option value="FAIL">Not indexed</option>
                  </select>
                  <input value={inspectUrl} onChange={(e) => setInspectUrl(e.target.value)}
                         placeholder="https://plugitai.com/ai-workforce"
                         className="h-9 w-72 bg-black border border-white/15 rounded px-3 text-xs outline-none focus:border-blue-400"
                         data-testid="input-inspect-url" />
                  <button onClick={runInspection} disabled={busy === "inspect" || !inspectUrl.trim()}
                          className="h-9 px-3 rounded bg-emerald-500 text-black font-black uppercase tracking-[0.22em] text-[10px] disabled:opacity-40"
                          data-testid="btn-inspect">
                    Inspect
                  </button>
                </div>
              </div>

              <div className="border border-white/10 rounded overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-white/40 bg-white/[0.03]">
                  <div className="col-span-5">URL</div>
                  <div className="col-span-2">Verdict</div>
                  <div className="col-span-3">Coverage</div>
                  <div className="col-span-2">Last crawl</div>
                </div>
                {filteredUrls.length === 0 ? (
                  <div className="p-4 text-sm text-white/50">No inspected URLs cached yet. Inspect one above.</div>
                ) : filteredUrls.map((u) => (
                  <div key={u.url} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-white/5 text-[12px] items-center" data-testid={`url-row-${u.url}`}>
                    <div className="col-span-5 truncate">
                      <a href={u.url} target="_blank" rel="noreferrer" className="hover:text-blue-400 truncate block">{u.url}</a>
                      <a href={`https://search.google.com/search-console/inspect?resource_id=${encodeURIComponent(propertyUrl)}&id=${encodeURIComponent(u.url)}`}
                         target="_blank" rel="noreferrer"
                         className="text-[10px] uppercase tracking-[0.22em] text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 mt-0.5">
                        Request Indexing in GSC <ExternalLink size={10} />
                      </a>
                    </div>
                    <div className="col-span-2">
                      <VerdictPill v={u.verdict} />
                    </div>
                    <div className="col-span-3 text-white/70">{u.coverage_state || "—"}</div>
                    <div className="col-span-2 text-white/60 inline-flex items-center gap-1">
                      <Clock size={10} />
                      {u.last_crawl_time ? new Date(u.last_crawl_time).toLocaleDateString() : "—"}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-white/40">
                Rate-limited by Google. If you hit quota, come back tomorrow or in the next quota window.
              </div>
            </section>
          </>
        )}

        <Link to="/admin" className="mt-8 inline-block text-[10px] uppercase tracking-[0.22em] text-white/40 hover:text-white" data-testid="back-to-admin">
          ← Back to admin
        </Link>
      </div>
    </main>
  );
}

function VerdictPill({ v }) {
  const map = {
    PASS:    { c: "#22C55E", label: "Indexed" },
    NEUTRAL: { c: "#4CC3FF", label: "Neutral" },
    PARTIAL: { c: "#F5B800", label: "Partial" },
    FAIL:    { c: "#EF4444", label: "Not indexed" },
  };
  const m = map[v] || { c: "#6B7280", label: v || "—" };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.24em] font-black"
          style={{ background: `${m.c}22`, color: m.c, border: `1px solid ${m.c}55` }}>
      {v === "PASS" && <CheckCircle2 size={10} />}
      {v === "FAIL" && <AlertTriangle size={10} />}
      {m.label}
    </span>
  );
}

function VerificationChecklist() {
  return (
    <div className="rounded border border-white/10 p-3 text-[11px] text-white/70 space-y-1" data-testid="verification-checklist">
      <div className="font-black text-white uppercase tracking-[0.22em] text-[10px] mb-1">Verification checklist</div>
      <div>1. Open <a className="text-blue-400 hover:underline" href="https://search.google.com/search-console" target="_blank" rel="noreferrer">search.google.com/search-console</a></div>
      <div>2. Add the property (Domain or URL-prefix) matching this tenant&#39;s live site.</div>
      <div>3. Complete DNS / HTML tag / file verification.</div>
      <div>4. Grant this Google account (the one above) &ldquo;Owner&rdquo; or &ldquo;Full user&rdquo; permission.</div>
      <div>5. Refresh this page &mdash; the property appears in the list below.</div>
    </div>
  );
}
